import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import BrowseIcon from '@mui/icons-material/FolderOpen';
import ImageIcon from '@mui/icons-material/Image';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';

const ActionsSaveImageTagToFile = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const [sourceTag, setSourceTag] = useState('extracted_image');
  const [targetPath, setTargetPath] = useState('');
  const [imageFormat, setImageFormat] = useState('png');
  const [sourceTagError, setSourceTagError] = useState('');
  const [targetPathError, setTargetPathError] = useState('');
  const sourceTagRef = useRef(null);
  const targetPathRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);
  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        const normalizedPath = String(selectedPath).replace(/[\\/]+$/, '');
        setTargetPath(`${normalizedPath}\\saved_image.${imageFormat}`);
        setTargetPathError('');
      }
    },
  });

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    handleConditionsChange,
    isSaveDisabled,
    settingsLoaded,
    id,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'saveimagetofile',
    onSaveStatusChange,
    customComparison: (initialData) => {
      if (!initialData) return false;
      return (
        String(initialData.sourceTag || initialData.inputTag || initialData.tagName || initialData.tag || 'extracted_image') === sourceTag &&
        String(initialData.targetPath || initialData.outputPath || initialData.path || '') === targetPath &&
        String(initialData.imageFormat || initialData.format || 'png').toLowerCase() === imageFormat
      );
    },
    customSaveData: () => ({
      sourceTag: sourceTag.trim(),
      targetPath: targetPath.trim(),
      imageFormat,
      useConditions: true,
      conditions,
    }),
    customForceRunData: () => ({
      sourceTag: sourceTag.trim(),
      targetPath: targetPath.trim(),
      imageFormat,
      useConditions: true,
      conditions,
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      setSourceTag(parsedData.sourceTag || parsedData.inputTag || parsedData.tagName || parsedData.tag || 'extracted_image');
      setTargetPath(parsedData.targetPath || parsedData.outputPath || parsedData.path || '');
      setImageFormat(String(parsedData.imageFormat || parsedData.format || 'png').toLowerCase() === 'jpg' ? 'jpg' : 'png');
    },
  });

  const handleBrowse = useCallback(() => {
    handleBrowseDirectory();
  }, [handleBrowseDirectory]);

  const handleCustomSave = useCallback(() => {
    let hasError = false;

    if (!String(sourceTag || '').trim()) {
      setSourceTagError('A source image tag is required.');
      hasError = true;
    } else if (sourceTagError) {
      setSourceTagError('');
    }

    if (!String(targetPath || '').trim()) {
      setTargetPathError('An output file path is required.');
      hasError = true;
    } else if (targetPathError) {
      setTargetPathError('');
    }

    if (!hasError) {
      handleSave();
    }
  }, [handleSave, sourceTag, sourceTagError, targetPath, targetPathError]);

  return (
    <ActionSettingsLayout
      title="Save Image Tag To File"
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleCustomSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      allowForceRunWithUnsavedChanges={settingsLoaded && !(typeof id === 'string' && id.startsWith('temp_'))}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Card sx={{ mb: 2, borderRadius: '12px', overflow: 'hidden' }}>
        <CardHeader
          title={<Typography sx={{ fontWeight: 700 }}>Export</Typography>}
          sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
        />
        <CardContent>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Source image tag</Typography>
              <TagEditor
                caseid={caseid}
                ref={sourceTagRef}
                value={sourceTag}
                onChange={setSourceTag}
                placeholder="extracted_image"
                singleLine
                multiline={false}
                tags={tags}
              />
              {sourceTagError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {sourceTagError}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Output file path</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <TagEditor
                    caseid={caseid}
                    ref={targetPathRef}
                    value={targetPath}
                    onChange={setTargetPath}
                    placeholder="saved_image.png"
                    singleLine
                    multiline={false}
                    tags={tags}
                  />
                </Box>
                <Button variant="outlined" onClick={handleBrowse} startIcon={<BrowseIcon />} sx={{ mt: 0.5 }}>
                  Browse
                </Button>
              </Box>
              {targetPathError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {targetPathError}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The extension is normalized to the selected format.
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Format</Typography>
              <FormControl fullWidth size="small">
                <Select value={imageFormat} onChange={(event) => setImageFormat(event.target.value)}>
                  <MenuItem value="png">PNG</MenuItem>
                  <MenuItem value="jpg">JPG</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The extension is normalized to the selected format.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {FolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsSaveImageTagToFile);
